import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import calendarService from '../services/calendarService';

export const useNotificationCookies = () => {
  const [readNotifications, setReadNotifications] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);

  // 📖 Carica notifiche già lette all'avvio
  const loadReadNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await calendarService.getReadNotifications();
      setReadNotifications(new Set(data.readNotificationIds || []));
    } catch (error) {
      console.error('Errore caricamento notifiche lette:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ✅ Marca notifiche come lette
  const markAsRead = useCallback(async (notificationIds) => {
    try {
      const ids = Array.isArray(notificationIds) ? notificationIds : [notificationIds];
      
      await calendarService.markNotificationsAsRead(ids);
      
      // Aggiorna stato locale
      setReadNotifications(prev => {
        const newSet = new Set(prev);
        ids.forEach(id => newSet.add(id));
        return newSet;
      });

      return true;
    } catch (error) {
      console.error('Errore marcatura notifiche come lette:', error);
      return false;
    }
  }, []);

  // 🔍 Verifica se notifica è già stata letta
  const isNotificationRead = useCallback((notificationId) => {
    return readNotifications.has(notificationId);
  }, [readNotifications]);

  // 🍞 Mostra notifiche come toast al login
  const showLoginToasts = useCallback(async (notifications) => {
    if (!notifications || notifications.length === 0) return;

    // Filtra solo notifiche non ancora lette
    const unreadNotifications = notifications.filter(
      notification => !isNotificationRead(notification.CAL_Id_CalendarioEventi)
    );

    if (unreadNotifications.length === 0) return;

    // Mostra toast per ogni notifica non letta
    unreadNotifications.forEach((notification, index) => {
      setTimeout(() => {
        const eventTime = new Date(notification.CAL_DataOraInizio);
        const now = new Date();
        const timeDiff = Math.round((eventTime - now) / (1000 * 60)); // minuti

        let timeText = '';
        if (timeDiff > 0) {
          if (timeDiff < 60) {
            timeText = `tra ${timeDiff} minuti`;
          } else {
            const hours = Math.floor(timeDiff / 60);
            timeText = `tra ${hours} ore`;
          }
        } else {
          timeText = 'in corso';
        }

        toast.info(
          `📅 ${notification.CAL_Titolo} ${timeText}`,
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            onClick: () => {
              // Marca come letta quando l'utente clicca sul toast
              markAsRead(notification.CAL_Id_CalendarioEventi);
            }
          }
        );
      }, index * 1000); // Ritardo di 1 secondo tra i toast
    });

    // Marca tutte le notifiche mostrate come lette
    const notificationIds = unreadNotifications.map(n => n.CAL_Id_CalendarioEventi);
    await markAsRead(notificationIds);

  }, [isNotificationRead, markAsRead]);

  // Carica notifiche lette all'avvio
  useEffect(() => {
    loadReadNotifications();
  }, [loadReadNotifications]);

  return {
    readNotifications: Array.from(readNotifications),
    isLoading,
    markAsRead,
    isNotificationRead,
    showLoginToasts,
    loadReadNotifications
  };
};